import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, firstValueFrom } from 'rxjs';
import { takeUntil, filter, take } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { AppState } from '@store/app.state';
import { selectAllFrames } from '@store/selectors/frame.selectors';
import { selectSettings } from '@store/selectors/settings.selector';
import { SettingsActions } from '@store/actions/settings.actions';
import { PersistenceService } from '@app/services/persistence.service';
import { generateUUID } from '@app/lib/utility/helper';
import {
  CollaborationService, CollabUser, PeerState, JoinRequest, RoomRole, CollabSettings,
  DEFAULT_COLLAB_SETTINGS
} from '@app/services/collaboration.service';
import { AuthService } from '@app/services/auth.service';
import { UserProfileModalComponent } from '@app/shared/user-profile-modal/user-profile-modal';
import { PxlNumberStepperDirective } from '@app/shared/pxl-number-stepper/pxl-number-stepper.directive';

type PanelView = 'closed' | 'lobby' | 'room';
type RoomTab   = 'peers' | 'requests' | 'settings';
type ModalMode = null | 'setup' | 'create' | 'join';

@Component({
  selector: 'app-collab-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, UserProfileModalComponent, PxlNumberStepperDirective],
  templateUrl: './collab-panel.html',
  styleUrls: ['./collab-panel.css']
})
export class CollabPanelComponent implements OnInit, OnChanges, OnDestroy {
  @Input() currentFrames: { id: string; pixels: string[][]; width: number; height: number }[] = [];
  @Input() currentCels: Record<string, string[][]> = {};
  @Input() currentLayers: any[] = [];
  @Input() currentActiveLayerId: string | null = null;
  @Output() authRequired  = new EventEmitter<void>();
  @Output() paymentRequired = new EventEmitter<{ feature: 'collab'; proceed: () => void }>();
  @Output() panelOpening  = new EventEmitter<void>(); // emitted before open so host can intercept
  _pendingOpenAllowed = false; // set to true by host after save check

  private destroy$ = new Subject<void>();
  /** Resolved once currentFrames has at least one frame with pixels */
  private framesReady$ = new Subject<void>();
  private framesReadyFired = false;

  panelView: PanelView = 'closed';
  modalMode: ModalMode = null;
  pendingAction: 'create' | 'join' | null = null;
  roomTab: RoomTab = 'peers';

  isInRoom = false;
  role: RoomRole = 'none';
  roomId: string | null = null;
  peers: PeerState[] = [];
  joinRequests: JoinRequest[] = [];
  performanceWarning: string | null = null;
  connectionStatus: 'disconnected' | 'connecting' | 'connected' = 'disconnected';
  localUser: CollabUser | null = null;
  settings: CollabSettings = { ...DEFAULT_COLLAB_SETTINGS };

  createPassword = '';
  joinRoomId = '';
  joinPassword = '';

  settingsEdit: CollabSettings = { ...DEFAULT_COLLAB_SETTINGS };
  settingsDirty = false;
  pendingRejoinAttempt = false;
  private creatorRejoinFallbackTimer: any = null;

  // Banned users list (populated from service)
  bannedUsers: string[] = [];
  chatMutedUsers: string[] = [];
  showAdminLeaveModal = false;
  leaveTransferTarget = '';

  // Toast feedback for copy actions
  copyToast: string | null = null;
  private _toastTimer: any = null;

  constructor(
    public collabSvc: CollaborationService,
    private auth: AuthService,
    private route: ActivatedRoute,
    private router: Router,
    private store: Store<AppState>,
    private persistence: PersistenceService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    // Fire framesReady$ the first time currentFrames has actual data
    if (!this.framesReadyFired && changes['currentFrames']) {
      const frames = changes['currentFrames'].currentValue as typeof this.currentFrames;
      if (frames && frames.length > 0) {
        this.framesReadyFired = true;
        this.framesReady$.next();
        this.framesReady$.complete();
      }
    }
  }

  ngOnInit(): void {
    this.localUser = this.collabSvc.localUser;

    this.collabSvc.isInRoom$.pipe(takeUntil(this.destroy$)).subscribe(v => {
      this.isInRoom = v;
      if (v) {
        clearTimeout(this.creatorRejoinFallbackTimer);
        this.creatorRejoinFallbackTimer = null;
        this.pendingRejoinAttempt = false;
        // Update browser URL to /room/ROOMID so address bar is always shareable
        if (this.roomId) this._updateRoomUrl(this.roomId);
        // Re-check pending requests now that we're confirmed in-room as admin
        if (this.role === 'admin' && this.joinRequests.length > 0) {
          this.roomTab = 'requests';
        }
        // If already connected (e.g. approved after waiting), close the panel now.
        // If not yet connected, the connectionStatus$ subscriber will close it once ready.
        if (this.connectionStatus === 'connected') {
          this.panelView = 'closed';
        } else {
          this.panelView = 'room';
        }
      }
    });
    this.collabSvc.role$.pipe(takeUntil(this.destroy$)).subscribe(v => {
      this.role = v;
      // Auto-switch to Requests tab if requests already queued when role resolves
      if (v === 'admin' && this.joinRequests.length > 0 && this.isInRoom) {
        this.roomTab = 'requests';
      }
      // If the user ends up in pending state (waiting for admin approval), open the
      // panel so they can see the "Waiting for approval" status - even on silent rejoin.
      if (v === 'pending' && this.panelView === 'closed') {
        this.panelView = 'room';
      }
    });
    this.collabSvc.roomId$.pipe(takeUntil(this.destroy$)).subscribe(v => this.roomId = v);
    this.collabSvc.peers$.pipe(takeUntil(this.destroy$)).subscribe(v => this.peers = v);
    this.collabSvc.joinRequests$.pipe(takeUntil(this.destroy$)).subscribe(v => {
      this.joinRequests = v;
      if (v.length > 0 && this.role === 'admin') {
        this.roomTab = 'requests';
        // FIX: Auto-open the panel if the admin has it closed and a request arrives
        if (this.panelView === 'closed' && this.isInRoom) {
          this.panelView = 'room';
        }
      }
    });
    this.collabSvc.performanceWarning$.pipe(takeUntil(this.destroy$)).subscribe(v => this.performanceWarning = v);
    this.collabSvc.connectionStatus$.pipe(takeUntil(this.destroy$)).subscribe(v => {
      this.connectionStatus = v;
      // Auto-close panel once fully connected AND approved into the room.
      // Do NOT close during the pending/waiting-for-approval phase - the user
      // needs to see the status panel while waiting.
      if (v === 'connected' && this.isInRoom && this.role !== 'pending' && this.panelView !== 'closed') {
        this.panelView = 'closed';
      }
    });
    this.collabSvc.collabSettings$.pipe(takeUntil(this.destroy$)).subscribe(v => {
      this.settings = v;
      this.settingsEdit = { ...v };
    });

    this.collabSvc.bannedUsers$.pipe(takeUntil(this.destroy$)).subscribe(v => {
      this.bannedUsers = v;
    });
    this.collabSvc.chatMutedUsers$.pipe(takeUntil(this.destroy$)).subscribe(v => {
      this.chatMutedUsers = v;
    });

    // ── Auto-rejoin after page refresh ──────────────────────────
    // Also handle /room/XXXX in the URL (invite links)
    const urlRoom = this._getRoomFromUrl();
    const last = this.collabSvc.getLastRoom();
    const target = urlRoom ?? (last?.roomId ? last : null);

    if (target) {
      const roomId = typeof target === 'string' ? target : target.roomId;
      // Determine if this is a page refresh (rejoin) or a fresh invite link:
      // If localStorage has the SAME room ID, it's a refresh - use the stored role.
      // If URL has a room ID but localStorage doesn't match, it's a fresh invite.
      const lastMatch = last && last.roomId === roomId;
      const isFromUrl = !!urlRoom && !lastMatch;
      const savedRole = lastMatch ? last!.role : 'pending';
      // isCreator: true only for the original room creator (stored explicitly at createRoom time).
      // Fallback for legacy entries that predate this field: assume admin = creator.
      const isCreator = lastMatch ? (last!.isCreator ?? (savedRole === 'admin')) : false;
      // Always rejoin on a page refresh; show lobby only for fresh invite links.
      const isRejoin = !isFromUrl;

      // Pre-fill room ID for any manual interaction needed
      this.joinRoomId = roomId;

      // Persist the invite room ID in sessionStorage immediately.
      // This makes it survive any URL changes (e.g. login redirects, Angular navigation)
      // so the join flow can complete even if the original URL is no longer available.
      if (isFromUrl) {
        sessionStorage.setItem('pxlvd_pending_room', roomId);
      }

      // Wait for auth to fully initialize before deciding whether to require login.
      // Supabase restores sessions asynchronously, so isAuthenticated may be false
      // for a split second even when the user has an active session. Checking it
      // synchronously causes logged-in users to see the login modal on invite links.
      this.auth.state$.pipe(
        filter(s => s.initialized),
        take(1),
        takeUntil(this.destroy$)
      ).subscribe(() => {
        this._handleRoomTarget(roomId, savedRole, isRejoin, isCreator);
      });
    }
  }

  private _handleRoomTarget(roomId: string, savedRole: RoomRole, isRejoin: boolean, isCreator = false): void {
    // Require authentication first
    if (!this.auth.isAuthenticated) {
      this.panelView = 'lobby';
      this.pendingAction = 'join';
      this.authRequired.emit();
      return;
    }
    this._requestCollabPaidAccess(() => {
      // Refresh localUser - CollaborationService populates it async via getProfile()
      // after auth state resolves, so it may not be set yet even for an existing user.
      this.localUser = this.collabSvc.localUser;

      // If localUser isn't loaded yet, poll for it before deciding what to do.
      // This prevents the profile modal from firing for existing users.
      if (!this.localUser) {
        this._waitForLocalUserThenHandleRoom(roomId, savedRole, isRejoin, 0, isCreator);
        return;
      }

      this._executeRoomTarget(roomId, savedRole, isRejoin, isCreator);
    });
  }

  /** Poll for localUser, then execute room target once ready */
  private _waitForLocalUserThenHandleRoom(roomId: string, savedRole: RoomRole, isRejoin: boolean, attempts: number, isCreator = false): void {
    this.localUser = this.collabSvc.localUser;
    if (this.localUser) {
      this._executeRoomTarget(roomId, savedRole, isRejoin, isCreator);
      return;
    }
    if (attempts < 12) {
      // Retry every 250ms (up to 3 seconds)
      setTimeout(() => this._waitForLocalUserThenHandleRoom(roomId, savedRole, isRejoin, attempts + 1, isCreator), 250);
    } else {
      // Truly no profile - fall back to profile setup
      if (isCreator) {
        this.panelView = 'lobby';
        this.pendingAction = 'create';
        this.modalMode = 'setup';
      } else {
        this.panelView = 'lobby';
        this.pendingAction = 'join';
        this.modalMode = 'setup';
      }
    }
  }

  /** Execute the room target once localUser is confirmed available */
  private _executeRoomTarget(roomId: string, savedRole: RoomRole, isRejoin: boolean, isCreator = false): void {
    const createCreatorRoom = () => {
      const doCreate = () => {
        if (!this.collabSvc.isInRoom$.value) {
          this.collabSvc.createRoom(roomId, this.currentFrames, {}, '', this.currentCels, this.currentLayers, this.currentActiveLayerId);
        }
      };
      if (this.currentFrames.length > 0) {
        doCreate();
      } else if (!this.framesReadyFired) {
        this.framesReady$.pipe(take(1)).subscribe(doCreate);
      } else {
        setTimeout(doCreate, 500);
      }
    };

    // IMPORTANT: On page refresh, the creator should rejoin the existing room state
    // from connected peers first. Recreating the room here can overwrite pixels with
    // whatever happens to be in local store at refresh time (often blank frames).
    if (savedRole === 'admin' && isCreator && !isRejoin) {
      // Only the original room creator recreates the room for non-rejoin flows.
      createCreatorRoom();
    } else if (isRejoin) {
      // Member or ex-admin rejoining after refresh - auto-join silently.
      // The service will auto-approve via _recheckApproval if their yAllowedUsers
      // entry is still valid; otherwise they queue as a pending join request.
      this.pendingRejoinAttempt = true;
      // Keep panel hidden during silent auto-rejoin - no flash of the lobby UI.
      // The role$ subscriber will open it if approval is needed (pending state),
      // and isInRoom$ handles the connected/auto-approved path.
      this.panelView = 'closed';
      sessionStorage.removeItem('pxlvd_pending_room');
      setTimeout(() => this.collabSvc.requestJoin(roomId, this.joinPassword, true, isCreator), 400);
      // Creator-specific fallback: if nobody is currently in the room, there may be
      // no peer state to rejoin. In that case, recreate the room from local frames.
      if (savedRole === 'admin' && isCreator) {
        clearTimeout(this.creatorRejoinFallbackTimer);
        this.creatorRejoinFallbackTimer = setTimeout(() => {
          if (this.collabSvc.isInRoom$.value) return;
          if (this.collabSvc.role$.value !== 'pending') return;
          createCreatorRoom();
        }, 4500);
      }
    } else {
      // Fresh invite link - show lobby with room ID pre-filled
      this.pendingRejoinAttempt = false;
      this.panelView = 'lobby';
      this.joinRoomId = roomId;
    }
  }

  // ── Panel toggle ──────────────────────────────────────────────
  /** Close the panel from outside (e.g. when the file menu opens). */
  closePanel(): void { this.panelView = 'closed'; }

  togglePanel(): void {
    // If panel is already open, close immediately - no save check needed
    if (this.panelView !== 'closed') {
      this.panelView = 'closed';
      return;
    }
    if (!this.auth.isAuthenticated) {
      this.authRequired.emit();
      return;
    }
    this._requestCollabPaidAccess(() => this._openPanelAfterPaid());
  }

  private _openPanelAfterPaid(): void {
    // FIX: Refresh localUser from service - it populates async from Supabase.
    // If still null, poll briefly before falling back to profile setup.
    this.localUser = this.collabSvc.localUser;
    if (!this.localUser) {
      this._waitForLocalUserThenOpen();
      return;
    }
    // If already in a room or pending, skip the save prompt - just open the panel
    if (this.isInRoom || this.isPending) {
      this.panelView = 'room';
      return;
    }
    // Emit to host - host will call proceedOpen() after any save prompt
    if (!this._pendingOpenAllowed) {
      this.panelOpening.emit();
      return;
    }
    this._pendingOpenAllowed = false;
    this.panelView = 'lobby';
  }

  /**
   * FIX: Poll for localUser (up to 2s) before falling back to profile modal.
   * This prevents the profile modal from firing for existing users whose
   * profile just hasn't loaded from Supabase yet.
   */
  private _waitForLocalUserThenOpen(attempts = 0): void {
    this.localUser = this.collabSvc.localUser;
    if (this.localUser) {
      // Profile loaded - proceed to open
      if (!this._pendingOpenAllowed) {
        this.panelOpening.emit();
      } else {
        this._pendingOpenAllowed = false;
        this.panelView = this.isInRoom || this.isPending ? 'room' : 'lobby';
      }
      return;
    }
    if (attempts < 8) {
      // Retry every 250ms (up to 2s total)
      setTimeout(() => this._waitForLocalUserThenOpen(attempts + 1), 250);
    } else {
      // Truly no profile - show setup modal with pending action to open panel after
      this.pendingAction = null;
      this._pendingOpenAfterProfile = true;
      this.modalMode = 'setup';
    }
  }

  /** Flag: after profile save, auto-open the collab panel */
  private _pendingOpenAfterProfile = false;

  /** Called by the host (app.ts) once any save prompt has been handled */
  proceedOpen(): void {
    this._pendingOpenAllowed = true;
    this.togglePanel();
  }

  // ── Lobby ─────────────────────────────────────────────────────
  openCreateRoom(): void {
    if (this.localUser) {
      // User already has a profile - generate room ID and create immediately
      const roomId = this.collabSvc.generateRoomId();
      this.collabSvc.createRoom(roomId, this.currentFrames, {}, this.createPassword, this.currentCels, this.currentLayers, this.currentActiveLayerId);
      this.panelView = 'room';
    } else {
      // No profile yet - prompt to set one up first
      this.pendingAction = 'create';
      this.modalMode = 'create';
    }
  }
  openJoinRoom():   void { this.pendingRejoinAttempt = false; this.pendingAction = 'join';   this.modalMode = 'join'; }
  openProfile():    void { this.modalMode = 'setup'; }

  onProfileConfirmed(ev: { user: CollabUser; roomId?: string; action: 'create' | 'join' | 'save' }): void {
    this.collabSvc.saveLocalUser(ev.user);
    this.localUser = ev.user;
    this.modalMode = null;

    if (ev.action === 'create') {
      const roomId = this.collabSvc.generateRoomId();
      this.collabSvc.createRoom(roomId, this.currentFrames, {}, this.createPassword, this.currentCels, this.currentLayers, this.currentActiveLayerId);
      this.panelView = 'room';
    } else if (ev.action === 'join') {
      const roomId = ev.roomId ?? this.joinRoomId;
      this.pendingRejoinAttempt = false;
      sessionStorage.removeItem('pxlvd_pending_room');
      this.collabSvc.requestJoin(roomId, this.joinPassword);
      this.panelView = 'room';
    } else if (ev.action === 'save' && this.pendingAction === 'join' && this.joinRoomId) {
      // Profile was just set up on arrival via invite link - now do the join
      this.pendingRejoinAttempt = false;
      sessionStorage.removeItem('pxlvd_pending_room');
      this.collabSvc.requestJoin(this.joinRoomId, this.joinPassword);
      this.panelView = 'room';
      this.pendingAction = null;
    } else if (ev.action === 'save' && this.pendingAction === 'create' && this.joinRoomId) {
      // Profile was just set up for admin rejoin
      const roomId = this.joinRoomId;
      this.framesReady$.pipe(take(1)).subscribe(() => {
        this.collabSvc.createRoom(roomId, this.currentFrames, {}, '', this.currentCels, this.currentLayers, this.currentActiveLayerId);
      });
      if (this.currentFrames.length > 0) {
        this.framesReadyFired = true;
        this.framesReady$.next();
        this.framesReady$.complete();
      }
      this.pendingAction = null;
    } else if (ev.action === 'save' && this._pendingOpenAfterProfile) {
      // FIX: Profile was saved from the toggle flow - now auto-open the panel
      this._pendingOpenAfterProfile = false;
      this.pendingAction = null;
      this.panelView = this.isInRoom || this.isPending ? 'room' : 'lobby';
    }
  }

  onProfileCancelled(): void { this.modalMode = null; this.pendingAction = null; this._pendingOpenAfterProfile = false; }

  /**
   * Called by app.ts after the user has completed login + username setup
   * following an invite-link visit that triggered an authRequired emit.
   */
  resumeAfterAuth(): void {
    const roomId = this.joinRoomId
      || this._getRoomFromUrl()
      || sessionStorage.getItem('pxlvd_pending_room')
      || '';
    if (!roomId) return;
    this.joinRoomId = roomId;

    // localUser is populated by an async getProfile() call in CollaborationService
    // after auth state changes. If it's not ready yet, poll briefly for it so we
    // don't fall into the profile-setup flow for a user who already has a username.
    this._joinWhenReady(roomId);
  }

  private _joinWhenReady(roomId: string, attempts = 0, isRejoin = false): void {
    // Refresh localUser from the service in case it has been populated since last check
    this.localUser = this.collabSvc.localUser;

    if (this.localUser) {
      this._requestCollabPaidAccess(() => {
        this.panelView = 'room';
        sessionStorage.removeItem('pxlvd_pending_room');
        setTimeout(() => this.collabSvc.requestJoin(roomId, this.joinPassword, isRejoin), 400);
      });
      return;
    }

    if (attempts < 10) {
      // Profile sync not done yet - retry in 300ms (up to 3 seconds total)
      setTimeout(() => this._joinWhenReady(roomId, attempts + 1, isRejoin), 300);
    } else {
      // Gave up waiting - user likely has no username yet, show setup
      this.panelView = 'lobby';
      this.pendingAction = 'join';
      this.modalMode = 'setup';
    }
  }

  private _requestCollabPaidAccess(proceed: () => void): void {
    this.paymentRequired.emit({ feature: 'collab', proceed });
  }

  // ── Room ──────────────────────────────────────────────────────
  leaveRoom(): void {
    clearTimeout(this.creatorRejoinFallbackTimer);
    this.creatorRejoinFallbackTimer = null;
    this.showAdminLeaveModal = false;
    this.leaveTransferTarget = '';
    this.pendingRejoinAttempt = false;
    sessionStorage.removeItem('pxlvd_pending_room');
    localStorage.removeItem('pxlvd_collab_autorejoin');
    this.collabSvc.leaveRoom();
    this.panelView = 'lobby';
    this.roomTab = 'peers';
    this._clearRoomUrl();
  }

  onLeaveClicked(): void {
    if (!this.isAdmin) {
      this.leaveRoom();
      return;
    }
    const candidates = this.transferCandidates;
    this.leaveTransferTarget = candidates[0]?.username ?? '';
    this.showAdminLeaveModal = true;
  }

  cancelAdminLeaveModal(): void {
    this.showAdminLeaveModal = false;
    this.leaveTransferTarget = '';
  }

  async closeRoomAndLeave(): Promise<void> {
    if (!this.isAdmin) return;
    if (!this.isRoomCreator) return;
    if (!confirm('Close room for everyone, auto-save snapshot, and leave?')) return;
    await this.autoSaveSnapshot();
    this.showAdminLeaveModal = false;
    this.panelView = 'lobby';
    this.roomTab = 'peers';
    this._clearRoomUrl();
    this.collabSvc.closeRoomByAdmin();
  }

  transferAdminAndLeave(): void {
    if (!this.isAdmin) return;
    const target = this.leaveTransferTarget;
    if (!target) return;
    const exists = this.transferCandidates.some(p => p.username === target);
    if (!exists) return;
    if (!confirm(`Transfer admin rights to ${target} and leave the room?`)) return;
    this.collabSvc.promoteUser(target, 'admin');
    this.showAdminLeaveModal = false;
    this.leaveRoom();
  }

  approveRequest(req: JoinRequest, asRole: 'member' | 'spectator' = 'member'): void {
    this.collabSvc.approveRequest(req.id, asRole);
  }

  rejectRequest(req: JoinRequest): void { this.collabSvc.rejectRequest(req.id); }

  kickUser(username: string): void {
    if (!confirm(`Kick and ban ${username} from the room? They will need to be unbanned before they can rejoin.`)) return;
    this.collabSvc.kickUser(username);
  }

  promoteUser(username: string): void { this.collabSvc.promoteUser(username, 'admin'); }
  demoteUser(username: string):  void { this.collabSvc.promoteUser(username, 'member'); }
  unbanUser(username: string):   void { this.collabSvc.unbanUser(username); }
  togglePeerReadonly(peer: PeerState): void {
    if (peer.role === 'admin') return;
    this.collabSvc.promoteUser(peer.username, peer.role === 'spectator' ? 'member' : 'spectator');
  }
  isPeerChatMuted(username: string): boolean {
    return this.chatMutedUsers.includes(username);
  }
  togglePeerChatMute(username: string): void {
    this.collabSvc.setUserChatMuted(username, !this.isPeerChatMuted(username));
  }

  // ── Settings ──────────────────────────────────────────────────
  onSettingChange(): void { this.settingsDirty = true; }

  saveSettings(): void {
    this.collabSvc.updateSettings(this.settingsEdit);
    this.settingsDirty = false;
    this._showToast('Settings saved ✓');
  }

  resetSettings(): void {
    this.settingsEdit = { ...this.settings };
    this.settingsDirty = false;
  }

  // ── Copy with toast feedback ──────────────────────────────────
  copySignalingCmd(): void {
    this._copy('npx y-webrtc-signaling --port 4444', 'Command copied!');
  }

  copyRoomId(): void {
    if (this.roomId) this._copy(this.roomId, 'Room ID copied!');
  }

  copyRoomLink(): void {
    if (!this.roomId) return;
    const url = `${window.location.origin}/room/${this.roomId}`;
    this._copy(url, 'Invite link copied!');
  }

  private _copy(text: string, toast: string): void {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text)
        .then(() => this._showToast(toast))
        .catch(() => this._fallbackCopy(text, toast));
    } else {
      this._fallbackCopy(text, toast);
    }
  }

  private _fallbackCopy(text: string, toast: string): void {
    const el = document.createElement('textarea');
    el.value = text;
    el.style.position = 'fixed'; el.style.opacity = '0';
    document.body.appendChild(el);
    el.select();
    try { document.execCommand('copy'); this._showToast(toast); }
    catch { this._showToast('Copy failed - select manually'); }
    document.body.removeChild(el);
  }

  private _showToast(msg: string): void {
    this.copyToast = msg;
    clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(() => { this.copyToast = null; }, 2200);
  }

  // ── URL helpers ───────────────────────────────────────────────
  private _getRoomFromUrl(): string | null {
    // Primary: Angular router param from route 'room/:roomId' - clean and reliable
    const routeParam = this.route.snapshot.params['roomId'];
    if (routeParam) return (routeParam as string).toUpperCase();
    // Fallback: read directly from pathname for /room/XXXX
    const pathMatch = window.location.pathname.match(/\/room\/([A-Z0-9]+)/i);
    if (pathMatch) return pathMatch[1].toUpperCase();
    // Legacy: old /room=XXXX format (handles links shared before this fix)
    const legacyMatch = window.location.pathname.match(/\/room[=]([A-Z0-9]+)/i)
      || window.location.pathname.match(/\/room%3D([A-Z0-9]+)/i);
    if (legacyMatch) return legacyMatch[1].toUpperCase();
    return null;
  }

  /** Update the browser URL to /room/ROOMID without triggering a navigation */
  private _updateRoomUrl(roomId: string): void {
    const targetPath = `/room/${roomId}`;
    if (window.location.pathname !== targetPath) {
      window.history.replaceState(null, '', targetPath);
    }
  }

  /** Clear the room URL back to / */
  private _clearRoomUrl(): void {
    if (window.location.pathname.startsWith('/room/')) {
      window.history.replaceState(null, '', '/');
    }
  }

  // ── Computed ──────────────────────────────────────────────────
  get totalUsers():   number  { return this.peers.length + (this.isInRoom ? 1 : 0); }
  get isPending():    boolean { return this.role === 'pending'; }
  get pendingStatusMessage(): string {
    if (this.pendingRejoinAttempt && this.settings.autoRejoin) {
      return 'Rejoining room… restoring your previous access.';
    }
    return 'Waiting for admin to approve your join request…';
  }

  get pendingStatusHint(): string {
    if (this.pendingRejoinAttempt && this.settings.autoRejoin) {
      return 'If your previous access is still valid, sync starts automatically. Otherwise admin approval is required.';
    }
    if (!this.settings.autoRejoin) {
      return 'Auto-rejoin is disabled for this room, so admin approval is required on refresh.';
    }
    return '';
  }

  get isAdmin():      boolean { return this.role === 'admin'; }
  get roomCreator():  string | null { return this.collabSvc.roomCreator$.value; }
  get isRoomCreator(): boolean { return !!this.localUser && this.localUser.username === this.roomCreator; }

  get statusColor(): string {
    return this.connectionStatus === 'connected'  ? '#69db7c'
         : this.connectionStatus === 'connecting' ? '#ffd43b' : '#ff6b6b';
  }

  get statusLabel(): string {
    return this.connectionStatus === 'connected'  ? 'Connected'
         : this.connectionStatus === 'connecting' ? 'Connecting…' : 'Disconnected';
  }

  get requestsBadge(): number { return this.joinRequests.length; }
  get transferCandidates(): PeerState[] {
    return this.peers.filter(p => p.role !== 'admin');
  }

  toolLabel(tool: string): string {
    return tool === 'erase' ? 'Erase' : tool === 'fill' ? 'Fill' : tool === 'pick' ? 'Pick' : 'Draw';
  }

  private async autoSaveSnapshot(): Promise<void> {
    const settings = await firstValueFrom(this.store.select(selectSettings).pipe(take(1)));
    const frames = await firstValueFrom(this.store.select(selectAllFrames).pipe(take(1)));
    const projectId = settings.currentProjectId ?? generateUUID();
    const baseName = (settings.projectName?.trim() || 'Untitled Project');
    const projectName = `${baseName} (Room Snapshot)`;
    await this.persistence.saveProject(projectId, projectName, frames, null);
    if (!settings.currentProjectId) {
      this.store.dispatch(SettingsActions.updateSettings({
        settings: { currentProjectId: projectId, projectName } as any
      }));
    }
    localStorage.setItem('pxlvd-last-project', projectId);
  }

  ngOnDestroy(): void {
    clearTimeout(this.creatorRejoinFallbackTimer);
    this.creatorRejoinFallbackTimer = null;
    clearTimeout(this._toastTimer);
    this.destroy$.next();
    this.destroy$.complete();
  }
}